import { action, computed, flow, makeObservable } from 'mobx';

import { hasValue } from 'src/shared/utils/common';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import type { RootStore } from 'src/store';
import type { Directories } from 'src/store/directories/directories.store';
import type { I18NextStore } from 'src/store/i18next/i18next-store';
import type { View } from 'src/store/views/views-store';

import type { GraphRigOperation } from '../graph-info-sidebar/entities/tab-with-stages-entities/rig-operation.entity';
import type { TabWithStages } from '../graph-info-sidebar/entities/tab-with-stages-entities/tab-with-stages.entity';
import type { TSidebarInfoView } from '../graph-info-sidebar/graph-info-sidebar';
import { GraphInfoSidebarStore } from '../graph-info-sidebar/graph-info-sidebar';
import { GeoLoadRigOperation } from '../well-form/entities/geological-load/geo-load-rig-operation.entity';
import { GeoLoadSection } from '../well-form/entities/geological-load/geo-load-section.entity';
import { GeoLoadStage } from '../well-form/entities/geological-load/geo-load-stage.entity';
import { GEOTASK_GEOLOAD_PROPERTIE_NAMES } from '../well-form/entities/geological-load/geological-load-service';
import { GeologicalLoad } from '../well-form/entities/geological-load/geological-load.entity';
import { getStageSlug } from '../well-form/entities/geological-load/utils';
import type { TGeologicalLoadData } from '../well-form/geological-load-tab/types';

import { WellStore } from './well.store';

export class WellInfoSidebarStore extends GraphInfoSidebarStore {
  protected readonly viewManager: View<TSidebarInfoView>;
  private readonly i18: I18NextStore;

  readonly directories: Directories;

  readonly wellStore: WellStore;

  constructor(rootStore: RootStore) {
    super({ rootStore });
    this.directories = rootStore.directories;
    this.viewManager = rootStore.views.wellSidebarView;
    this.i18 = rootStore.i18;

    this.wellStore = new WellStore(rootStore.editing, rootStore.notifications);

    makeObservable(this);
  }

  @flow.bound
  async *init() {
    await this.loadView();
    yield;
  }

  @flow.bound
  async *loadWell(wellId: string | number) {
    if (!this.viewProvider) return;

    this.isLoading = true;

    try {
      await this.wellStore.fetchWell(wellId);
      await this.viewProvider.setValues(this.wellStore.selectedWell);
      yield;
    } catch (e) {
      yield;
      console.error(e);
    } finally {
      this.isLoading = false;
    }
  }

  @computed
  get rigOperations(): GraphRigOperation[] {
    return (this.viewProvider?.tabs.find((tab) => tab.fieldId === 'stages') as TabWithStages)?.rigOperations ?? [];
  }

  @computed
  get geoTaskGeoLoad() {
    if (this.rigOperations.length) {
      return null;
    }

    const geoTaskGeoLoadInitialValue = (() => {
      if (!this.wellStore.selectedWell) {
        return {};
      }

      const initialGeoTaskData: TGeologicalLoadData = {};

      for (const name of GEOTASK_GEOLOAD_PROPERTIE_NAMES) {
        const value = (() => {
          const rawValue = this.wellStore.selectedWell[name];

          if (!hasValue(rawValue)) {
            return null;
          }

          return parsePrimitivesFromString(rawValue as string);
        })();

        if (hasValue(value)) {
          const key = name.split('.')[1] as keyof TGeologicalLoadData;

          // TODO: улучшить типизацию
          initialGeoTaskData[key] = value as any;
        }
      }

      return initialGeoTaskData;
    })();

    return new GeologicalLoad(geoTaskGeoLoadInitialValue);
  }

  @computed
  get geologicalLoadRigOperations() {
    if (!this.rigOperations.length) {
      return [];
    }

    return this.rigOperations.map((ro) => {
      // TODO: Вынести эти сущности из well-form
      const ROGL = new GeoLoadRigOperation(ro.entityId, ro.item);

      const glStages = [];

      for (const stage of ro.stages) {
        const stageTypeId = stage.item['wellLifeCycleStageId'];

        if (!stageTypeId || typeof stageTypeId !== 'number') {
          continue;
        }

        const stageSlug = getStageSlug(this.directories, stageTypeId);

        if (stageSlug !== 'drilling') {
          continue;
        }

        const GLStage = new GeoLoadStage(ro.entityId, stage.entityId, stage.item);

        const GLSections = stage.sections.map((section) => {
          return new GeoLoadSection(ro.entityId, stage.entityId, section.entityId, section.item);
        });

        GLStage.setSections(GLSections);

        glStages.push(GLStage);
      }

      ROGL.setStages(glStages);

      return ROGL;
    });
  }

  @action.bound
  setIsLoading(is: boolean) {
    this.isLoading = is;
  }
}

export namespace WellInfoSidebar {
  export enum Tabs {
    mainInfo = 'mainData',
    geoLoad = 'geologicalLoad',
    stages = 'stages',
  }
}
